import React, { useRef, useState } from "react";
import useFetchState from "../hooks/useFetchState";
import { api } from "../api";
import "../styles/resource-canvas.css";

export default function ResourceCanvas() {
  const [state, fetchState, setState] = useFetchState();
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isResizing, setIsResizing] = useState(null);
  const canvasRef = useRef(null);

  const getCoords = (e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleDragStart = (e, item, type) => {
    setDraggedItem({ ...item, dragType: type });
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const { x, y } = getCoords(e);
    if (!draggedItem) return;

    if (draggedItem.dragType === "resource") {
      const tempId = `temp-${Date.now()}`;
      const tempResource = {
        ...draggedItem,
        uniqueId: tempId,
        x,
        y,
        name: `${draggedItem.type}-${state.resources.length + 1}`,
        containerId: null,
      };
      setState((prev) => ({
        ...prev,
        resources: [...prev.resources, tempResource],
      }));

      try {
        const real = await api.addResource({ resource: draggedItem, x, y });
        setState((prev) => ({
          ...prev,
          resources: prev.resources.map((r) =>
            r.uniqueId === tempId ? real : r
          ),
        }));
      } catch {
        setState((prev) => ({
          ...prev,
          resources: prev.resources.filter((r) => r.uniqueId !== tempId),
        }));
      }
    }

    if (draggedItem.dragType === "container") {
      const tempId = `temp-${Date.now()}`;
      const tempContainer = {
        ...draggedItem,
        uniqueId: tempId,
        x,
        y,
        width: 300,
        height: 250,
        name: `${draggedItem.type}-${state.containers.length + 1}`,
        locationId: null,
      };
      setState((prev) => ({
        ...prev,
        containers: [...prev.containers, tempContainer],
      }));

      try {
        const real = await api.addContainer({ container: draggedItem, x, y });
        setState((prev) => ({
          ...prev,
          containers: prev.containers.map((c) =>
            c.uniqueId === tempId ? real : c
          ),
        }));
      } catch {
        setState((prev) => ({
          ...prev,
          containers: prev.containers.filter((c) => c.uniqueId !== tempId),
        }));
      }
    }

    setDraggedItem(null);
  };

  const handleItemDragStart = (e, item) => {
    setSelectedItem(item);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleCanvasDropMove = async (e) => {
    e.preventDefault();
    if (!selectedItem) return;
    const { x, y } = getCoords(e);

    if ("containerId" in selectedItem) {
      setState((prev) => ({
        ...prev,
        resources: prev.resources.map((r) =>
          r.uniqueId === selectedItem.uniqueId ? { ...r, x, y } : r
        ),
      }));
      await api.moveResource({ id: selectedItem.uniqueId, x, y });
    } else {
      setState((prev) => ({
        ...prev,
        containers: prev.containers.map((c) =>
          c.uniqueId === selectedItem.uniqueId ? { ...c, x, y } : c
        ),
      }));
      await api.moveContainer({ id: selectedItem.uniqueId, x, y });
    }
    setSelectedItem(null);
    fetchState();
  };

  const handleDeleteResource = async (id) => {
    setState((p) => ({
      ...p,
      resources: p.resources.filter((r) => r.uniqueId !== id),
    }));
    await api.deleteResource(id).catch(() => {});
  };

  const handleDeleteContainer = async (id) => {
    setState((p) => ({
      ...p,
      containers: p.containers.filter((c) => c.uniqueId !== id),
    }));
    await api.deleteContainer(id);
    fetchState();
  };

  const handleDeleteLocation = async (id) => {
    setState((p) => ({
      ...p,
      locations: p.locations.filter((l) => l.uniqueId !== id),
    }));
    await api.deleteLocation(id);
    fetchState();
  };

  const addLocation = async (loc) => {
    const newLoc = await api.addLocation({ location: loc });
    setState((p) => ({ ...p, locations: [...p.locations, newLoc] }));
  };

  const handleResizeStart = (e, id) => {
    e.stopPropagation();
    setIsResizing(id);
  };

  const handleResizeMove = (e) => {
    if (!isResizing) return;
    const { x, y } = getCoords(e);
    setState((prev) => ({
      ...prev,
      containers: prev.containers.map((c) => {
        if (c.uniqueId === isResizing) {
          return {
            ...c,
            width: Math.max(200, x - c.x),
            height: Math.max(150, y - c.y),
          };
        }
        return c;
      }),
    }));
  };

  const handleResizeEnd = async () => {
    if (!isResizing) return;
    const container = state.containers.find((c) => c.uniqueId === isResizing);
    if (container) {
      await api.resizeContainer({
        id: isResizing,
        width: container.width,
        height: container.height,
      });
    }
    setIsResizing(null);
    fetchState();
  };

  return (
    <div className="resource-main">
      <aside className="sidebar">
        <h3 className="sidebar-title">Elementos</h3>

        {/* UBICACIONES */}
        <section className="section">
          <h4 className="section-title">Ubicaciones</h4>

          {(state.locationTemplates || []).map((loc) => (
            <div
              key={loc.id}
              className="resource-card location-card"
              onClick={() => addLocation(loc)}
            >
              <span dangerouslySetInnerHTML={{ __html: loc.icon }} />
              <div className="text">
                <div className="name">{loc.name}</div>
              </div>
            </div>
          ))}
        </section>

        {/* AGRUPACIONES */}
        <section className="section">
          <h4 className="section-title">Agrupaciones</h4>

          {(state.availableContainers || []).map((c) => (
            <div
              key={c.id}
              className="resource-card"
              draggable
              onDragStart={(e) => handleDragStart(e, c, "container")}
            >
              <div dangerouslySetInnerHTML={{ __html: c.icon }} />
              <div className="label">{c.type}</div>
            </div>
          ))}
        </section>

        {/* RECURSOS POR CATEGORÍA */}
        {Object.entries(state.availableResources || {}).map(
          ([category, items]) => (
            <section key={category} className="section">
              <h4 className="section-title">{category}</h4>
              <div className="grid">
                {items.map((r) => (
                  <div
                    key={r.id}
                    className="resource-card"
                    draggable
                    onDragStart={(e) => handleDragStart(e, r, "resource")}
                  >
                    <div dangerouslySetInnerHTML={{ __html: r.icon }} />
                    <div className="label">{r.type}</div>
                    <div className="provider">{r.provider}</div>
                  </div>
                ))}
              </div>
            </section>
          )
        )}
      </aside>

      <div
        ref={canvasRef}
        className="canvas-area"
        onDragOver={handleDragOver}
        onDrop={draggedItem ? handleDrop : handleCanvasDropMove}
        onMouseMove={isResizing ? handleResizeMove : undefined}
        onMouseUp={handleResizeEnd}
        onMouseLeave={handleResizeEnd}
      >
        <header className="canvas-header">
          <h2>Arquitectura de Infraestructura</h2>
          <div className="actions">
            <button className="btn">Guardar</button>
            <button className="btn">Exportar</button>
            {state.resources.length +
              state.containers.length +
              state.locations.length >
              0 && (
              <button
                className="btn-danger"
                onClick={async () => {
                  await api.clearAll();
                  fetchState();
                }}
              >
                Limpiar Todo
              </button>
            )}
          </div>
        </header>

        {/* LOCATIONS */}
        {state.locations.map((loc) => (
          <div
            key={loc.uniqueId}
            className="location-box"
            style={{
              left: loc.x,
              top: loc.y,
              width: loc.width,
              height: loc.height,
              border: `3px dashed ${loc.color}`,
              background: `${loc.color}08`,
            }}
          >
            <div className="header">
              <span>
                {loc.icon} {loc.name}
              </span>
              <button onClick={() => handleDeleteLocation(loc.uniqueId)}>
                X
              </button>
            </div>
          </div>
        ))}

        {/* CONTAINERS */}
        {state.containers.map((cont) => (
          <div
            key={cont.uniqueId}
            className="container-box"
            draggable
            onDragStart={(e) => handleItemDragStart(e, cont)}
            style={{
              left: cont.x,
              top: cont.y,
              width: cont.width,
              height: cont.height,
              border: `2px solid ${cont.color}`,
              background: `${cont.color}10`,
            }}
          >
            <div className="header">
              <span>
                {cont.icon} {cont.name}
              </span>
              <button onClick={() => handleDeleteContainer(cont.uniqueId)}>
                X
              </button>
            </div>
            <div
              className="resize-handle"
              onMouseDown={(e) => handleResizeStart(e, cont.uniqueId)}
            >
              ↘
            </div>
          </div>
        ))}

        {/* RESOURCES */}
        {state.resources.map((res) => (
          <div
            key={res.uniqueId}
            className="placed-resource"
            draggable
            onDragStart={(e) => handleItemDragStart(e, res)}
            style={{
              left: res.x,
              top: res.y,
              border: `2px solid ${res.color}`,
            }}
          >
            <div className="content">
              <span className="icon">{res.icon}</span>
              <div className="info">
                <div className="name">{res.name}</div>
                <div className="type">
                  {res.type} • {res.provider}
                </div>
              </div>
            </div>
            <button onClick={() => handleDeleteResource(res.uniqueId)}>
              X
            </button>
          </div>
        ))}

        {/* EMPTY STATE */}
        {state.resources.length === 0 &&
          state.containers.length === 0 &&
          state.locations.length === 0 && (
            <div className="empty-state">
              <div className="big-icon">Canvas</div>
              <p>Arrastra elementos para comenzar</p>
            </div>
          )}
      </div>
    </div>
  );
}
